import { Component, computed, effect, inject, ChangeDetectionStrategy } from '@angular/core';
import { AttrPath, DoorstroomResultaat, Niveau, Table } from '@cumlaude/metadata';
import { isNil, partition, range } from 'lodash-es';
import { Observable } from 'rxjs';
import { Level, Path } from '../../services/data-tree';
import { doorstroom_afk } from '../../services/labels';
import { ColumnDef, TableModel } from '../../shared/components/table/table/table.model';
import {
	BasicFilterExpression,
	BasicFilterExpressionType,
	DataOptions,
	DataResponse,
	DataService,
	DoorstroomMeasure,
	ExportDataOptions,
	ExportFilter,
	FilterExpression,
} from '../../services/data.service';
import { maxOverMapped, noAgg0, SingleAggregator, sumOver } from '../../services/aggregation';
import { att, percOfRow } from '../../services/measures';
import { deelVeilig, generateCssClassForString } from '@cumlaude/shared-utils';
import { BarInfo } from '../../services/stacked-bars';
import { createMeasureColumn, DataRow } from '../../shared/dashboard/data-tree-table/data-tree-table';
import { Router } from '@angular/router';
import { BarchartTableConfig } from '../../shared/dashboard/barchart-table/barchart-table-config';
import { Attributes, getLevelFilters, SelectionConfig } from '../../shared/dashboard/base-dashboard/base-dashboard-config';
import { DashboardContext, DataRequestContext } from '../../shared/dashboard/base-dashboard/dashboard-context';
import { PartitionMeasure, VbarchartTableComponent } from '../../shared/dashboard/vbarchart-table/vbarchart-table.component';
import { DashboardVariant, DoorstroomWeergave } from '../../services/weergave-opties';
import { TooltipElement } from '@cumlaude/shared-components-overlays';
import { formatDecimal } from '@cumlaude/shared-pipes';
import { PsName } from '../../services/page-state.service';
import { BarchartTableComponent } from '../../shared/dashboard/barchart-table/barchart-table.component';
import { CheckboxComponent } from '@cumlaude/shared-components-inputs';
import { DashboardHeaderComponent } from '../../dashboard-header/dashboard-header.component';
import { FilterPanelComponent } from '../../filter-panel/filter-panel.component';
import { DashboardContainerComponent } from '../../layout/dashboard-container/dashboard-container.component';
import { FilterBarComponent } from '../../filter-bar/filter-bar.component';
import { WeergaveOptieComponent } from '../../shared/components/weergave-optie/weergave-optie.component';
import { FilterName } from '../../services/filter-name';
import { LegendaComponent } from '../../legenda/legenda.component';
import { ToggleButtonsComponent } from '@cumlaude/shared-components-buttons';

export interface DoorstroomI extends Attributes {
	ds_is_succesvol: string;
	ds_is_bevorderd: string;
	'ds_fk_lb_van.lb_nm_niveau_volgend_sj': Niveau | null;
	'ds_fk_lb_van.lb_nr_leerjaar_volgend_sj': string | null;
	ds_nm_idu: DoorstroomResultaat | null;
	ds_fun_nm_brinvest_overgang?: string | null;
	ds_is_uitstroom_vavo: string;
	ds_is_uitstroom_extern: string;
	ds_is_prognose: string;
	ds_m_nr_bevorderd_weging: number;
	ds_m_nr_bevorderd_leerlingen: number;
	ds_m_nr_niet_bevorderd_weging: number;
	ds_m_nr_niet_bevorderd_leerlingen: number;
	ds_m_nr_succesvol_weging: number;
	ds_m_nr_succesvol_leerlingen: number;
	ds_m_nr_niet_succesvol_weging: number;
	ds_m_nr_niet_succesvol_leerlingen: number;
	ds_m_nr_opstroom_weging: number;
	ds_m_nr_opstroom_leerlingen: number;
	ds_m_nr_afstroom_weging: number;
	ds_m_nr_afstroom_leerlingen: number;
	ds_m_nr_bekend_weging: number;
	ds_m_nr_bekend_leerlingen: number;
	ds_m_nr_onbekend_weging: number;
	ds_m_nr_onbekend_leerlingen: number;
	ds_m_nr_weging: number;
	ds_m_nr_leerlingen: number;
}

export interface DoorstroomA extends Attributes {
	bevorderd_weging: number;
	bevorderd_lln: number;
	niet_bevorderd_weging: number;
	niet_bevorderd_lln: number;
	succesvol_weging: number;
	succesvol_lln: number;
	niet_succesvol_weging: number;
	niet_succesvol_lln: number;
	opstroom_weging: number;
	opstroom_lln: number;
	afstroom_weging: number;
	afstroom_lln: number;
	bekend_weging: number;
	bekend_lln: number;
	onbekend_weging: number;
	onbekend_lln: number;
	weging: number;
	ds_is_prognose: number;
	ds_m_nr_leerlingen: number;
}

export const OPTIONAL_2_DECIMALS = '1.0-2';

export type SuccesConditie = {
	attr: AttrPath;
	weging: 'succesvol_weging' | 'bevorderd_weging';
	lln: 'succesvol_lln' | 'bevorderd_lln';
};

@Component({
	selector: 'app-doorstroom',
	templateUrl: './doorstroom.component.html',
	styleUrls: ['./doorstroom.component.scss'],
	imports: [
		DashboardContainerComponent,
		FilterPanelComponent,
		DashboardHeaderComponent,
		CheckboxComponent,
		BarchartTableComponent,
		VbarchartTableComponent,
		FilterBarComponent,
		WeergaveOptieComponent,
		LegendaComponent,
		ToggleButtonsComponent,
	],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DoorstroomComponent extends BarchartTableConfig<DoorstroomI, DoorstroomA> {
	protected readonly router = inject(Router);
	protected readonly dataService = inject(DataService);

	fixedSubgroups = computed(() => (this.toonBrinOvergang() ? this.doorstroomLevelsMetOvergang : this.doorstroomLevelsZonderOvergang));

	historieGroups = computed(() => this.groeperingService.huidigeGroeperingen().slice(0, -1));

	partitionGroups = computed(() => [...this.groeperingService.huidigeGroeperingen(), ['ds_nm_schooljaar_van'] as AttrPath]);

	historieSubgroups = computed(() => [
		...this.groeperingService.huidigeGroeperingen().slice(-1),
		['ds_nm_schooljaar_van'] as AttrPath,
		...this.fixedSubgroups(),
	]);

	override availableGroups: AttrPath[] = [
		['ds_fk_ll', 'll_nm_basisschooladvies'],
		['ds_nm_klas_van'],
		['ds_nr_leerjaar_van'],
		['ds_fk_ilt_van', 'ilt_nm_niveau'],
		['ds_nm_opleiding_van'],
		['ds_fk_ilt_van', 'ilt_nm_opleiding'],
		['ds_fk_ilt_van', 'ilt_abb_profiel'],
		['ds_fk_vs_van', 'vs_nm_vestiging'],
		['ds_fk_ll', 'll_nm_geslacht'],
	];

	actueelFilters: FilterName[] = [
		'ds_nm_schooljaar_van',
		'ds_fk_lb_van.lb_co_brin',
		'ds_fk_vs_van.vs_nm_vestiging',
		'ds_fk_ilt_van.ilt_nm_niveau',
		'ds_nr_leerjaar_van',
		'ds_nm_idu',
	];

	historieFilters: FilterName[] = [
		'x_doorstroom_schooljaar_historie', //
		'x_doorstroom_multiselect_schooljaar',
		...this.actueelFilters,
	];

	permanentFilterExpressions: FilterExpression[] = [
		new BasicFilterExpression(['ds_is_relevante_doorstroom'], 1),
		new BasicFilterExpression(['ds_nr_weging'], 0, '>'),
	];

	doorstroomLevelsZonderOvergang: AttrPath[] = [
		this.succesConditie.attr,
		['ds_nm_idu'],
		['ds_fk_lb_van', 'lb_nm_niveau_volgend_sj'],
		['ds_fk_lb_van', 'lb_nr_leerjaar_volgend_sj'],
		['ds_is_prognose'],
		['ds_is_uitstroom_vavo'],
		['ds_is_uitstroom_extern'],
		['ds_is_bevorderd'],
	];

	doorstroomLevelsMetOvergang: AttrPath[] = [
		...this.doorstroomLevelsZonderOvergang, //
		['ds_fun_nm_brinvest_overgang'],
	];

	toonBrinOvergang = this.qp.signal('brin-overgang');

	variant = this.qp.signal('variant');

	rollupLevels = computed(() => {
		return range(
			0,
			(this.variant() === DashboardVariant.ACTUEEL ? this.groeperingService.huidigeAantalGroeperingen() : this.partitionGroups().length) + 1
		);
	});

	partitionMeasure = computed(() => {
		const weergaveOptie = this.weergaveOptie();
		return {
			type: 'percentage',
			getValue: percOfRow<keyof DoorstroomA, DoorstroomA>(this.getAtt(weergaveOptie)[0], 'bekend_weging'),
		} as PartitionMeasure<DoorstroomA>;
	});

	dashboardName!: string;

	legendaExcludes: DoorstroomResultaat[] = [DoorstroomResultaat.GEEN];

	weergaveOptie = this.qp.signal('doorstroomweergave');

	constructor() {
		super();
		this.subscriptions.push(this.urlService.routeData$.subscribe((routeData) => (this.dashboardName = routeData.title)));
		effect(() => {
			this.weergaveOptie();
			this.filterService.refresh();
		});
	}

	factTable = Table.fac_ds_doorstroom;

	getData(options: DataOptions): Observable<DataResponse<number[]>> {
		return this.dataService.getDoorstroomData({
			...options,
			m: [DoorstroomMeasure.LEERLINGEN, DoorstroomMeasure.DOORSTROOM_AANTALLEN],
			r: this.rollupLevels(),
		});
	}

	override getExportData(options: ExportDataOptions) {
		return this.dataService.getDoorstroomExportData(options);
	}

	override getDisplayOptions(): ExportFilter[] {
		return [{ label: 'Weergave', value: this.weergaveOptie() }];
	}

	protected override singleAggregators: Partial<{ [ai in keyof DoorstroomA]: SingleAggregator<DoorstroomI, DoorstroomA[ai]> }> = {
		bevorderd_weging: noAgg0<'ds_m_nr_bevorderd_weging', DoorstroomI>('ds_m_nr_bevorderd_weging'),
		bevorderd_lln: noAgg0<'ds_m_nr_bevorderd_leerlingen', DoorstroomI>('ds_m_nr_bevorderd_leerlingen'),
		niet_bevorderd_weging: noAgg0<'ds_m_nr_niet_bevorderd_weging', DoorstroomI>('ds_m_nr_niet_bevorderd_weging'),
		niet_bevorderd_lln: noAgg0<'ds_m_nr_niet_bevorderd_leerlingen', DoorstroomI>('ds_m_nr_niet_bevorderd_leerlingen'),
		succesvol_weging: noAgg0<'ds_m_nr_succesvol_weging', DoorstroomI>('ds_m_nr_succesvol_weging'),
		succesvol_lln: noAgg0<'ds_m_nr_succesvol_leerlingen', DoorstroomI>('ds_m_nr_succesvol_leerlingen'),
		niet_succesvol_weging: noAgg0<'ds_m_nr_niet_succesvol_weging', DoorstroomI>('ds_m_nr_niet_succesvol_weging'),
		niet_succesvol_lln: noAgg0<'ds_m_nr_niet_succesvol_leerlingen', DoorstroomI>('ds_m_nr_niet_succesvol_leerlingen'),
		opstroom_weging: noAgg0<'ds_m_nr_opstroom_weging', DoorstroomI>('ds_m_nr_opstroom_weging'),
		opstroom_lln: noAgg0<'ds_m_nr_opstroom_leerlingen', DoorstroomI>('ds_m_nr_opstroom_leerlingen'),
		afstroom_weging: noAgg0<'ds_m_nr_afstroom_weging', DoorstroomI>('ds_m_nr_afstroom_weging'),
		afstroom_lln: noAgg0<'ds_m_nr_afstroom_leerlingen', DoorstroomI>('ds_m_nr_afstroom_leerlingen'),
		bekend_weging: noAgg0<'ds_m_nr_bekend_weging', DoorstroomI>('ds_m_nr_bekend_weging'),
		bekend_lln: noAgg0<'ds_m_nr_bekend_leerlingen', DoorstroomI>('ds_m_nr_bekend_leerlingen'),
		onbekend_weging: noAgg0<'ds_m_nr_onbekend_weging', DoorstroomI>('ds_m_nr_onbekend_weging'),
		onbekend_lln: noAgg0<'ds_m_nr_onbekend_leerlingen', DoorstroomI>('ds_m_nr_onbekend_leerlingen'),
		weging: sumOver<'ds_m_nr_weging', DoorstroomI, number>('ds_m_nr_weging'),
		ds_is_prognose: maxOverMapped<DoorstroomI>((v) => Number(v.ds_is_prognose)),
		ds_m_nr_leerlingen: noAgg0<'ds_m_nr_leerlingen', DoorstroomI>('ds_m_nr_leerlingen'),
	};

	override partitionBarData(rowRoot: Level<DoorstroomA, number[]>): Path<DoorstroomA, number[]>[][] {
		const [bevorderd, nietBevorderd] = partition(rowRoot.c, (c) => c.k === '1');
		return [...bevorderd.map((c) => c.r), ...nietBevorderd.map((c) => c.r)];
	}

	/**
	 * Bepaalt wanneer de doorstroom van een leerling succesvol is. Subclasses van dit dashboard kunnen een alternatief attribuut en waarde bepalen
	 */
	protected get succesConditie(): SuccesConditie {
		return {
			attr: ['ds_is_succesvol'],
			weging: 'succesvol_weging',
			lln: 'succesvol_lln',
		};
	}

	override makeBar(
		attrs: DoorstroomI,
		path: Path<DoorstroomA, number[]>,
		context: DashboardContext<DoorstroomI, DoorstroomA, DoorstroomComponent>
	): BarInfo {
		const {
			'ds_fk_lb_van.lb_nr_leerjaar_volgend_sj': nr_leerjaar_naar,
			ds_nm_idu,
			ds_is_prognose,
			ds_fun_nm_brinvest_overgang,
			ds_is_uitstroom_vavo,
			ds_is_uitstroom_extern,
			'ds_fk_lb_van.lb_nm_niveau_volgend_sj': nm_niveau_naar,
			ds_m_nr_weging,
		} = attrs;

		const isOvergang = !isNil(ds_fun_nm_brinvest_overgang);
		const brinvest_overgang = isOvergang ? ` (${ds_fun_nm_brinvest_overgang})` : '';
		const vavo = ds_is_uitstroom_vavo === '1' ? ' (VAVO)' : '';
		const extern = ds_is_uitstroom_extern === '1' && ds_nm_idu !== DoorstroomResultaat.GESLAAGD ? ' (ext)' : '';
		const prognose = Number(ds_is_prognose) ? ' (prognose)' : '';

		const tooltipElements: TooltipElement[] = [];
		const descriptionElements: string[] = [];
		if (nm_niveau_naar || nr_leerjaar_naar || (ds_nm_idu !== DoorstroomResultaat.GESLAAGD && ds_nm_idu !== DoorstroomResultaat.AFGEWEZEN)) {
			tooltipElements.push(
				{ label: `Niveau`, value: `${this.displayService.display(nm_niveau_naar) + vavo + extern}` },
				{ label: `Leerjaar`, value: `${this.displayService.display(nr_leerjaar_naar)}` }
			);
			descriptionElements.push(
				this.displayService.describe(nm_niveau_naar, ['ds_fk_lb_van', 'lb_nm_niveau_volgend_sj']),
				this.displayService.describe(nr_leerjaar_naar, ['ds_fk_lb_van', 'lb_nr_leerjaar_volgend_sj'])
			);
		}

		if (isOvergang) {
			tooltipElements.push({ label: `Vestigingscode-overgang`, value: `${ds_fun_nm_brinvest_overgang}` });
		}

		let position = this.getPartitionLevelPosition(context.groupNames);
		const pathElement = path[position].a;
		const aantal = ds_nm_idu == null ? pathElement.onbekend_weging : pathElement.bekend_weging;

		tooltipElements.push(
			{ label: `IDU`, value: `${this.displayService.display(ds_nm_idu) + prognose}` },
			{
				label: this.getCountRecordsHeader(),
				value: `${formatDecimal(ds_m_nr_weging, OPTIONAL_2_DECIMALS)} van ${formatDecimal(aantal, OPTIONAL_2_DECIMALS)}`,
			}
		);
		descriptionElements.push(this.displayService.describe(ds_nm_idu, ['ds_nm_idu']));

		if (ds_nm_idu !== null) {
			const percentage = Math.round(deelVeilig(ds_m_nr_weging, aantal) * 100);
			tooltipElements.push({ label: `Leerlingen (%)`, value: `${percentage}%` });
		}

		const description = descriptionElements.join(', ') + brinvest_overgang + vavo + extern + prognose;

		return {
			...super.makeBar(attrs, path, context),
			size: ds_m_nr_weging,
			text: doorstroom_afk(attrs) + brinvest_overgang + vavo + extern + prognose,
			description,
			className: generateCssClassForString(ds_nm_idu ? ds_nm_idu + prognose : null),
			tooltip: tooltipElements,
		};
	}

	getPartitionLevelPosition(groupNames: string[]) {
		let position = groupNames.length;
		if (this.variant() === DashboardVariant.HISTORIE) position = position + 2;
		return position;
	}

	override createMeasureColumns(context: DashboardContext<DoorstroomI, DoorstroomA, DoorstroomComponent>): ColumnDef<DataRow<DoorstroomA>>[] {
		if (this.variant() === DashboardVariant.HISTORIE) return [];

		return [
			...this.getWeergaveColums(context),
			this.createLeerlingColumn('Leerlingen', att('bekend_weging'), { context, format: OPTIONAL_2_DECIMALS }, att('bekend_lln'), [
				new BasicFilterExpression(['ds_nm_idu'], null, '<>'),
			]),
			createMeasureColumn('Onbekend', att('onbekend_weging'), {
				context,
				format: OPTIONAL_2_DECIMALS,
				clickHandler: (path) => this.handleUitzonderingRedirect(path, context),
			}),
		];
	}

	override isHistorieBatchVariant(): boolean {
		return this.variant() === DashboardVariant.HISTORIE && this.groeperingService.huidigeAantalGroeperingen() > 0;
	}

	protected getAtt(weergaveOptie: DoorstroomWeergave): [keyof DoorstroomA, keyof DoorstroomA] {
		switch (weergaveOptie) {
			case DoorstroomWeergave.SUCCESVOL:
				return ['succesvol_weging', 'succesvol_lln'];
			case DoorstroomWeergave.NIET_SUCCESVOL:
				return ['niet_succesvol_weging', 'niet_succesvol_lln'];
			case DoorstroomWeergave.BEVORDERD:
				return ['bevorderd_weging', 'bevorderd_lln'];
			case DoorstroomWeergave.NIET_BEVORDERD:
				return ['niet_bevorderd_weging', 'niet_bevorderd_lln'];
			case DoorstroomWeergave.OPSTROOM:
				return ['opstroom_weging', 'opstroom_lln'];
			case DoorstroomWeergave.AFSTROOM:
				return ['afstroom_weging', 'afstroom_lln'];
		}
	}

	private getExtraSelectionFilters(): FilterExpression[] {
		const doorstroomWeergave = this.weergaveOptie();
		const bevorderdSuccesvolAll = [
			DoorstroomWeergave.SUCCESVOL,
			DoorstroomWeergave.NIET_SUCCESVOL,
			DoorstroomWeergave.BEVORDERD,
			DoorstroomWeergave.NIET_BEVORDERD,
		];
		const bevorderdSuccesvolPositief = [DoorstroomWeergave.SUCCESVOL, DoorstroomWeergave.BEVORDERD];
		const bevorderd = [DoorstroomWeergave.BEVORDERD, DoorstroomWeergave.NIET_BEVORDERD];

		if (bevorderdSuccesvolAll.includes(doorstroomWeergave)) {
			const attr = <AttrPath>(bevorderd.includes(doorstroomWeergave) ? ['ds_is_bevorderd'] : ['ds_is_succesvol']);
			const type = bevorderdSuccesvolPositief.includes(doorstroomWeergave) ? '=' : '<>';
			return this.getBevorderdSuccesvolFilters(attr, type);
		} else return this.getDoorstroomStatusFilters(this.getDoorstroomResultaat(doorstroomWeergave));
	}

	private getDoorstroomResultaat(weergaveOptie: DoorstroomWeergave): DoorstroomResultaat {
		switch (weergaveOptie) {
			case DoorstroomWeergave.OPSTROOM:
				return DoorstroomResultaat.OPSTROOM;
			case DoorstroomWeergave.AFSTROOM:
				return DoorstroomResultaat.AFSTROOM;
		}
		throw new Error('Geen mapping van weergave naar doorstroomresultaat');
	}

	protected getWeergaveColums(context: DashboardContext<DoorstroomI, DoorstroomA, DoorstroomComponent>): ColumnDef<DataRow<DoorstroomA>>[] {
		const doorstroomWeergave = this.weergaveOptie();
		const [weging, lln] = this.getAtt(doorstroomWeergave);
		const extraFilters = this.getExtraSelectionFilters();

		return [
			this.createLeerlingColumn(
				'% ' + doorstroomWeergave,
				percOfRow(weging, 'bekend_weging'),
				{ context, dataType: 'percentage' },
				att(lln),
				extraFilters
			),
			this.createLeerlingColumn(doorstroomWeergave, att(weging), { context, format: OPTIONAL_2_DECIMALS }, att(lln), extraFilters),
		];
	}

	protected getCountRecordsHeader() {
		return `# lln`;
	}

	getDoorstroomStatusFilters(doorstroomStatus: DoorstroomResultaat) {
		return [new BasicFilterExpression(['ds_nm_idu'], doorstroomStatus, '=')];
	}

	protected getBevorderdSuccesvolFilters(attr: AttrPath, type: BasicFilterExpressionType) {
		return [new BasicFilterExpression(['ds_nm_idu'], null, '<>'), new BasicFilterExpression(attr, '1', type)];
	}

	protected getBekendFilters() {
		return [new BasicFilterExpression(['ds_nm_idu'], null, '<>')];
	}

	protected handleUitzonderingRedirect(
		path: Path<DoorstroomA, number[]>,
		context: DashboardContext<DoorstroomI, DoorstroomA, DoorstroomComponent>
	) {
		const params = {
			from: this.dashboardName,
		};

		for (const levelFilter of getLevelFilters(context, path)) {
			Object.assign(params, this.filterService.encodeFilterValue(<FilterName>levelFilter.attr.join('.'), [levelFilter.val]));
		}

		this.urlService.redirectDoorklik([this.getUitzonderingUrl()], params);
	}

	getUitzonderingUrl() {
		return '/details/uitzondering/doorstroom';
	}

	override enrichTableModel(
		_context: DashboardContext<DoorstroomI, DoorstroomA, DoorstroomComponent>,
		tableModel: TableModel<DataRow<DoorstroomA>>
	) {
		tableModel.showFooters = this.variant() === DashboardVariant.ACTUEEL;
	}

	override onContextCreated(context: DashboardContext<DoorstroomI, DoorstroomA, DoorstroomComponent>): void {
		this.pageStateService.dispatch(PsName.prognose, String(Boolean(context.dataRoot?.a.ds_is_prognose)));
	}

	override getSelectionConfig(context: DataRequestContext<DoorstroomA>): SelectionConfig<DoorstroomA> | undefined {
		return {
			...super.getSelectionConfig(context)!,
			getSize: att('ds_m_nr_leerlingen'),
			extraFilters: [],
		};
	}

	protected readonly DashboardVariant = DashboardVariant;
	protected readonly DoorstroomWeergave = DoorstroomWeergave;
}
